import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { PCheckboxModule } from '@panaya/ui/p-checkbox';
import { PTooltipModule } from '@panaya/ui/p-tooltip';
import { CanvasEmptyStateComponent, CanvasFrameStore } from '@panaya/ai-chat/features';
import { DraftEntity, EntityType } from '@panaya/ai-chat/shared';
import { entityTitle } from '../../mapping/apply-mapping';
import { CanvasEntityMappingRegistry } from '../../mapping/canvas-entity-mapping.registry';
import { DraftStore } from '../../draft.store';
import { DraftEntityComponent } from '../draft-entity/draft-entity.component';

interface PanelView {
    entity: DraftEntity;
    /** The draft's entity type, which picks the mapping. */
    entityType: EntityType;
    /** The panel's place in the list, shown before the title. */
    position: number;
    /** `null` when the data has no title: the untitled text shows. */
    title: string | null;
    automationKey: string;
    expanded: boolean;
    selected: boolean;
}

/** The panels whose open state differs from their default, kept per draft so that a new draft starts from the default again. */
interface ToggledPanels {
    draftId: string | null;
    ids: ReadonlySet<number>;
}

/**
 * The `collection` display: the agent's intro line, then one expansion panel per entity, with a checkbox that decides
 * which entities the primary action takes. The first panel starts open and the others closed; while the entities are
 * being created every panel is closed and cannot be opened.
 */
@Component({
    standalone: true,
    selector: 'p-ai-chat-collection-display',
    imports: [TranslateModule, MatExpansionModule, PTooltipModule, PCheckboxModule, CanvasEmptyStateComponent, DraftEntityComponent],
    templateUrl: './collection-display.component.html',
    styleUrl: './collection-display.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionDisplayComponent {
    private readonly mappings = inject(CanvasEntityMappingRegistry);

    private readonly frameStore = inject(CanvasFrameStore);

    private readonly toggled = signal<ToggledPanels>({ draftId: null, ids: new Set() });

    private readonly draftId = computed(() => this.draftStore.draft()?.draft_id ?? null);

    private readonly toggledIds = computed((): ReadonlySet<number> => {
        const toggled = this.toggled();

        return toggled.draftId === this.draftId() ? toggled.ids : new Set();
    });

    protected readonly draftStore = inject(DraftStore);

    protected readonly creating = computed(() => this.frameStore.status() === 'creating');

    protected readonly intro = computed(() => this.draftStore.draft()?.intro ?? null);

    /** Checkboxes wait while a revise call runs or the entities are being created. */
    protected readonly selectionDisabled = computed(() => this.draftStore.status() === 'filling' || this.creating());

    protected readonly panels = computed((): PanelView[] => {
        const draft = this.draftStore.draft();

        if (!draft) {
            return [];
        }

        const mapping = this.mappings.find(draft.entity_type);

        const selected = this.draftStore.selectedIdSet();

        const toggled = this.toggledIds();

        return draft.entities.map((entity, index) => ({
            entity,
            entityType: draft.entity_type,
            position: index + 1,
            title: entityTitle(entity.data, mapping),
            automationKey: String(entity.id),
            expanded: !this.creating() && (index === 0) !== toggled.has(entity.id),
            selected: selected.has(entity.id)
        }));
    });

    /** The registration's host extras, which the frame hands every display; this display reads none of them. */
    public readonly params = input<Record<string, unknown>>({});

    /**
     * Records a panel the user opened or closed. Material also reports a change made through the `expanded` binding,
     * such as every panel closing when creation starts; those already match the panel's state and are ignored.
     */
    public setExpanded(panel: PanelView, expanded: boolean): void {
        if (expanded === panel.expanded) {
            return;
        }

        const next = new Set(this.toggledIds());

        if (!next.delete(panel.entity.id)) {
            next.add(panel.entity.id);
        }

        this.toggled.set({ draftId: this.draftId(), ids: next });
    }

    public select(id: number, selected: boolean): void {
        this.draftStore.setSelected(id, selected);
    }
}
